'use client';

import { clsx } from 'clsx';
import { useState } from 'react';

import { Typography, Icons, LinkButton, FontIcons } from '@monoimin/atoms';

import { useTranslations } from '@monoimin/intl';
import { STORAGE_KEYS } from '@monoimin/constants';

import { Popup } from '@monoimin/molecules/Popup';

import { getFieldCustomId } from '../utils';
import { RenderInput } from '../utils/render';
import { RenderButton } from '../utils/RenderButton';
import { CustomVariantProps } from '../types';

const SubscribePopup = ({
  formConfig,
  locale,
  onInput,
  onSubmit,
  formValues,
  formValuesById,
  formRef,
  buttonProps,
  subscribePopupFormDict,
  hideForm,
  isLoading,
}: CustomVariantProps) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const getMessage = useTranslations(subscribePopupFormDict);

  return (
    <Popup
      dataTestId='subscribe-popup'
      closedStorageKey={STORAGE_KEYS.SUBSCRIBE_POPUP.CLOSED}
      preventOpeningStorageKey={STORAGE_KEYS.SUBSCRIBE_POPUP.SENT}
      classNames={{
        container: clsx(
          'popup subscribe-popup shadow-popup flex flex-col items-center pt-5 pb-4 max-lg:pb-9 px-5  transition ease-in-out',
          'data-[closed]:opacity-0',
          'data-[enter]:duration-100 data-[enter]:data-[closed]:translate-y-full',
          'data-[leave]:duration-300 data-[leave]:data-[closed]:translate-y-full',
          {
            'bg-white min-h-[170px]': !showSuccessMessage,
            'bg-blue-500': showSuccessMessage,
          }
        ),
        closeButtonIcon: clsx('w-6 h-6', { 'bg-white': showSuccessMessage }),
        closeButton: 'top-3 right-3',
      }}
    >
      {({ onPreventOpen }) => {
        return (
          <>
            {showSuccessMessage && (
              <div className='flex lg:max-w-[750px] gap-2'>
                <Icons
                  icon='info'
                  sizeClassName='w-5 h-5'
                  className='bg-white shrink-0 mt-1'
                  isInteractive
                />
                <Typography className='text-white body hidden md:block'>
                  <span className='mr-1 font-bold'>
                    {getMessage('thankYouForSubscribing')}
                  </span>
                  {getMessage('toStartReceiving')}
                </Typography>
                <Typography className='text-white body md:hidden font-bold'>
                  {getMessage('pleaseConfirmSubscription')}
                </Typography>
              </div>
            )}
            {!showSuccessMessage && (
              <>
                <Typography className='body font-bold mb-4 md:mb-1 md:text-center pr-10 lg:pr-0 lg:max-w-[750px]'>
                  {getMessage(hideForm ? 'titleAdditional' : 'title')}
                </Typography>
                <Typography
                  className={clsx(
                    'body-small mb-2.5 pr-10 lg:pr-0 lg:max-w-[750px] md:text-center',
                    {
                      'hidden md:block': !hideForm,
                    }
                  )}
                >
                  {getMessage(hideForm ? 'subtitleAdditional' : 'subtitle')}
                </Typography>
              </>
            )}
            {!hideForm && !showSuccessMessage && (
              <form
                className='flex flex-row gap-3 items-start relative overflow-hidden sm:max-w-[580px] w-full'
                ref={formRef}
                onSubmit={async (event) => {
                  const { success } = await onSubmit(event);

                  if (success) {
                    onPreventOpen();
                    setShowSuccessMessage(true);
                  }
                }}
                onInput={onInput}
              >
                {formConfig.fields.map((field) => {
                  const customId = getFieldCustomId(field);
                  return (
                    <RenderInput
                      key={customId}
                      {...field}
                      customId={customId}
                      formValues={formValues}
                      formValuesById={formValuesById}
                      locale={locale}
                      withPlaceholder
                      classNames={{
                        input: 'w-full', // TODO это должен быть проп container
                        inputWrapper: 'h-12.5 md:w-full',
                        inputTag: 'text-caption! pt-3.5!',
                        label: 'hidden ',
                      }}
                    />
                  );
                })}
                <RenderButton
                  {...buttonProps}
                  variant='primary'
                  size='small'
                  loading={isLoading}
                  theme='blue'
                  className='md:px-7 min-h-full '
                >
                  <span className='whitespace-nowrap md:block hidden'>
                    {formConfig.buttonText ||
                      formConfig.button.text ||
                      getMessage('subscribe')}
                  </span>
                  <FontIcons icon='arrowRight' className='md:hidden ' />
                </RenderButton>
              </form>
            )}
            {hideForm && (
              <LinkButton
                className='min-h-full'
                size='small'
                icon='arrowRight'
                iconClassName='md:hidden ml-0 mt-0'
                href={getMessage('buttonUrlAdditional')}
              >
                {getMessage('buttonTitleAdditional')}
              </LinkButton>
            )}
          </>
        );
      }}
    </Popup>
  );
};

export default SubscribePopup;
